import type {AddressCountryCode} from "@/src/modules/core/types/address-reference";
import {coerceAddressCountryCode, formatAddressCountryLabel} from "@/src/modules/core/lib/address-reference";

import type {Property} from "../types";

type PropertyAddressSource = {
  number?: number | string | null;
  street?: string | null;
  unit_number?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
  /** @deprecated API read alias — normalize on ingest */
  zip_code?: string | number | null;
};

/** Resolve postal code from API (`zipcode`) or legacy `zip_code`. */
export function resolvePropertyZipcode(
  property: Pick<PropertyAddressSource, "zipcode" | "zip_code">,
): string {
  const zip = property.zipcode ?? property.zip_code;
  if (zip == null) return "";
  return String(zip).trim();
}

/** `number street` — first line in list/card address blocks. */
export function formatPropertyAddressStreetLine(property: PropertyAddressSource): string {
  return [property.number, property.street]
    .filter((value) => value != null && String(value).trim() !== "")
    .map(String)
    .join(" ")
    .trim();
}

/** `city zipcode, country` — second line in list/card address blocks. */
export function formatPropertyAddressLocalityLine(property: PropertyAddressSource): string {
  const locality = [property.city?.trim(), resolvePropertyZipcode(property)]
    .filter((part): part is string => Boolean(part))
    .join(" ");

  const countryLabel = formatAddressCountryLabel(coerceAddressCountryCode(property.country));
  return locality ? `${locality}, ${countryLabel}` : countryLabel;
}

/** `number street, unit_number, city, state, zipcode, country` — omits empty segments. */
export function formatPropertyAddressLine(property: PropertyAddressSource): string {
  const streetLine = formatPropertyAddressStreetLine(property);

  const parts = [
    streetLine || null,
    property.unit_number?.trim() || null,
    property.city?.trim() || null,
    property.state?.trim() || null,
    resolvePropertyZipcode(property) || null,
  ].filter((part): part is string => Boolean(part));

  const line = parts.join(", ");
  const countryLabel = formatAddressCountryLabel(coerceAddressCountryCode(property.country));
  return line ? `${line}, ${countryLabel}` : countryLabel;
}

export function normalizePropertyAddressFields<T extends PropertyAddressSource>(
  row: T,
): T & Pick<Property, "zipcode" | "unit_number" | "country"> {
  const {zip_code: _legacyZip, ...rest} = row as T & {zip_code?: string | number | null};
  return {
    ...rest,
    zipcode: resolvePropertyZipcode(row),
    unit_number: row.unit_number?.trim() || null,
    country: coerceAddressCountryCode(row.country),
  } as T & Pick<Property, "zipcode" | "unit_number" | "country">;
}

export function normalizePropertyAddressForWrite(fields: {
  street: string;
  number: string | number;
  unit_number?: string | null;
  city: string;
  state: string;
  zipcode: string;
  country?: AddressCountryCode;
}) {
  const country = coerceAddressCountryCode(fields.country);
  return {
    street: fields.street,
    number: typeof fields.number === "number" ? fields.number : Number(fields.number),
    unit_number: fields.unit_number?.trim() || null,
    city: fields.city,
    state: fields.state,
    zipcode: fields.zipcode.trim(),
    ...(country !== "US" ? {country} : {}),
  };
}

export function normalizeProperty<T extends Property & {zip_code?: string | number | null}>(
  row: T,
): Property {
  return normalizePropertyAddressFields(row) as Property;
}
